import { buildInviteLinkCopyText } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { trackEvent } from '@/utils/analytics';

import { isReferralBannerVisible } from './referral-banner-eligibility';
import { REFERRAL_CREDIT } from './referral.constants';
import { useReferralCodeQuery } from './referral.query';

export const NewChatReferralCard = observer(() => {
  const { data, isLoading } = useReferralCodeQuery();
  const [copied, setCopied] = useState(false);
  const { setHash } = useHashRoute();
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visible = isReferralBannerVisible();

  useEffect(() => {
    if (visible && data?.referral_code) {
      trackEvent('REFERRAL_CTA_VIEWED', { source: 'referral_banner' });
    }
  }, [data?.referral_code, visible]);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  if (!visible || isLoading || !data?.referral_code) {
    return null;
  }

  return (
    <motion.div
      className="pointer-events-auto"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
    >
      <XStack
        onClick={() => {
          trackEvent('REFERRAL_CTA_CLICKED', { source: 'referral_banner' });
          setHash('invite');
        }}
        className={cn(
          'group items-center gap-4 rounded-xl py-3 pl-4 pr-2',
          'bg-gradient-to-r from-brand-jordy-blueA/5 to-transparent',
          'hover:from-brand-jordy-blueA/10',
          'border border-border hover:border-border-strong',
          'transition-all',
          'max-w-[480px] w-full'
        )}
      >
        <Icons.refer className="size-5 text-brand-jordy-blue shrink-0 origin-bottom motion-safe:group-hover:animate-gift-wiggle" />
        <YStack className="flex-1 gap-0.5">
          <span className="text-sm font-medium">Gift 2 months free</span>
          <span className="text-xs text-foreground-muted">
            Earn {REFERRAL_CREDIT.PAYMENT} in credit when they subscribe
          </span>
        </YStack>
        <XStack className="items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="w-24"
            onClick={async (e) => {
              e.stopPropagation();
              const link = buildInviteLinkCopyText(data.referral_code);
              try {
                await navigator.clipboard.writeText(link);
              } catch {
                toast.error('Failed to copy link');
                return;
              }
              setCopied(true);
              trackEvent('REFERRAL_INVITE_COPIED', {
                referral_code: data.referral_code,
                source: 'referral_banner',
              });
              if (copiedTimeoutRef.current)
                clearTimeout(copiedTimeoutRef.current);
              copiedTimeoutRef.current = setTimeout(
                () => setCopied(false),
                2000
              );
            }}
          >
            {copied ? 'Copied!' : 'Copy Invite'}
          </Button>
          <IconButton
            icon={Icons.cancel}
            size="xs"
            variant="ghost"
            tooltip="Dismiss"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              trackEvent('REFERRAL_CTA_DISMISSED', {
                source: 'referral_banner',
              });
              uiStore.dismissReferralCard();
            }}
          />
        </XStack>
      </XStack>
    </motion.div>
  );
});
